import React, { useState } from 'react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../hooks/useAuth';
import { useAuth as useMainAuth } from '@/contexts/AuthContext';
import { cn } from '../lib/utils';
import {
  Folder,
  Search,
  HelpCircle,
  Play,
  Terminal,
  ChevronRight,
  Grid3X3,
  LogOut,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from './ui/alert-dialog';
import { PROGRAMS, PROGRAM_MAP } from '../utils/programs';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';
import { makeOverrideIcon } from './AppIcon';

const SearchMenuIcon = makeOverrideIcon('search', Search);
const HelpMenuIcon = makeOverrideIcon('help-menu', HelpCircle);
const RunMenuIcon = makeOverrideIcon('run-menu', Play);
const AllProgramsMenuIcon = makeOverrideIcon('all-programs', Grid3X3);
const LogOutMenuIcon = makeOverrideIcon('log-out', LogOut);
const StartMenuFolderIcon = makeOverrideIcon('folder', Folder);

interface StartMenuProps {
  onClose: () => void;
  onOpenApp: (appId: string, title: string) => void;
}

export const StartMenu: React.FC<StartMenuProps> = ({ onClose, onOpenApp }) => {
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const { user, profile } = useAuth();
  const { signOut } = useMainAuth();
  const navigate = useNavigate();
  const [showExitDialog, setShowExitDialog] = useState(false);

  const accentTitlebarEnabled = (() => {
    const root = (document.querySelector('.mapleos-root') as HTMLElement) || document.documentElement;
    const v =
      root.style.getPropertyValue('--window-header') ||
      getComputedStyle(root).getPropertyValue('--window-header');
    return !String(v).trim().includes('var(');
  })();

  const menuItems = [
    { id: 'ai-center', label: 'AI Center', icon: PROGRAM_MAP['ai-center']?.icon },

    { id: 'file-explorer', label: 'File Explorer', icon: PROGRAM_MAP['file-explorer']?.icon },
    { id: 'app-store', label: 'Maple Market', icon: PROGRAM_MAP['app-store']?.icon },

    { id: 'office-suite', label: 'Office Suite', icon: PROGRAM_MAP['office-suite']?.icon },
    { id: 'task-manager', label: 'Task Manager', icon: PROGRAM_MAP['task-manager']?.icon },
    { id: 'terminal', label: 'Terminal', icon: PROGRAM_MAP['terminal']?.icon || Terminal },
    { id: 'control-panel', label: 'Control Panel', icon: PROGRAM_MAP['control-panel']?.icon },
    { id: 'display-properties', label: 'Display Properties', icon: PROGRAM_MAP['display-properties']?.icon },
  ];

  const displayName =
    (profile as any)?.full_name ||
    profile?.display_name ||
    profile?.username ||
    (user?.email ? user.email.split('@')[0] : '') ||
    'User';

  const initials = displayName
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');

  const sortedPrograms = [...PROGRAMS].sort((a, b) => a.label.localeCompare(b.label));

  const groupedPrograms = sortedPrograms.reduce(
    (acc, program) => {
      if (program.folder) {
        const existingFolder = acc.folders.find(([name]) => name === program.folder);
        if (existingFolder) {
          existingFolder[1].push(program);
        } else {
          acc.folders.push([program.folder, [program]]);
        }
      } else {
        acc.ungrouped.push(program);
      }
      return acc;
    },
    { folders: [] as [string, typeof PROGRAMS][], ungrouped: [] as typeof PROGRAMS }
  );

  const hasFolders = groupedPrograms.folders.length > 0;

  const handleLogout = async () => {
    onClose();
    setShowExitDialog(false);
    await signOut();
    navigate('/auth');
  };

  return (
    <>
      <div className="fixed bottom-12 left-0 z-[2147483647] flex items-end">
        <div className="flex">
          <div className="bg-card border border-border rounded-tr shadow-xl w-64 max-h-[calc(100vh-80px)] flex flex-col">
            <div
              className={cn(
                "bg-gradient-window p-4 rounded-tr",
                accentTitlebarEnabled ? "text-primary-foreground" : "text-primary"
              )}
            >
              <button
                type="button"
                className="flex w-full items-center gap-3 text-left"
                onClick={() => {
                  onOpenApp('profile', 'Profile');
                  onClose();
                }}
              >
                <Avatar className="h-10 w-10 border border-border/50">
                  <AvatarImage src={(profile as any)?.avatar_url || undefined} alt={displayName} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{displayName}</div>
                  <div className="text-xs opacity-80 truncate">{user?.email}</div>
                </div>
              </button>
            </div>

            <div className="p-2 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    className="w-full justify-start gap-3 mb-1 hover:bg-primary hover:text-primary-foreground"
                    onClick={() => {
                      onOpenApp(item.id, item.label);
                      onClose();
                    }}
                  >
                    <Icon size={28} />
                    {item.label}
                  </Button>
                );
              })}

              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mb-1 hover:bg-primary hover:text-primary-foreground"
                onClick={() => {
                  onOpenApp('search', 'Search');
                  onClose();
                }}
              >
                <SearchMenuIcon size={28} />
                Search
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mb-1 hover:bg-primary hover:text-primary-foreground"
                onClick={() => {
                  onOpenApp('help', 'Help');
                  onClose();
                }}
              >
                <HelpMenuIcon size={28} />
                Help
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mb-1 hover:bg-primary hover:text-primary-foreground"
                onClick={() => {
                  onOpenApp('run', 'Run');
                  onClose();
                }}
              >
                <RunMenuIcon size={28} />
                Run
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-between gap-3 mb-1 hover:bg-primary hover:text-primary-foreground"
                onClick={() => setShowAllPrograms(!showAllPrograms)}
              >
                <div className="flex items-center gap-3">
                  <AllProgramsMenuIcon size={28} />
                  All Programs
                </div>
                <ChevronRight size={18} />
              </Button>

              <Separator className="my-2" />

              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mb-1 hover:bg-orange-500 hover:text-white"
                onClick={() => {
                  setShowExitDialog(true);
                }}
              >
                <LogOutMenuIcon size={28} />
                Log Out
              </Button>

            </div>
          </div>

          {showAllPrograms && (
            <div className="bg-card border border-l-0 border-border rounded-tr shadow-xl w-64 max-h-[calc(100vh-80px)] flex flex-col">
              <div
                className={cn(
                  "bg-gradient-window p-3 rounded-tr",
                  accentTitlebarEnabled ? "text-primary-foreground" : "text-primary"
                )}
              >
                <span className="font-semibold text-sm">All Programs</span>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {!hasFolders && (
                    <>
                      {sortedPrograms.map((program) => {
                        const Icon = program.icon;
                        return (
                          <Button
                            key={program.id}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-3 text-sm hover:bg-primary hover:text-primary-foreground"
                            onClick={() => {
                              onOpenApp(program.id, program.label);
                              onClose();
                            }}
                          >
                            <Icon size={28} />
                            {program.label}
                          </Button>
                        );
                      })}
                    </>
                  )}

                  {hasFolders && (
                    <>
                      {groupedPrograms.ungrouped.map((program) => {
                        const Icon = program.icon;
                        return (
                          <Button
                            key={program.id}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-3 text-sm hover:bg-primary hover:text-primary-foreground"
                            onClick={() => {
                              onOpenApp(program.id, program.label);
                              onClose();
                            }}
                          >
                            <Icon size={28} />
                            {program.label}
                          </Button>
                        );
                      })}

                      {groupedPrograms.folders.map(([folderName, programs]) => (
                        <Collapsible key={folderName}>
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-between gap-3 text-sm hover:bg-primary hover:text-primary-foreground"
                            >
                              <div className="flex items-center gap-3">
                                <StartMenuFolderIcon size={28} />
                                {folderName}
                              </div>
                              <ChevronRight size={16} />
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pl-6 space-y-1 mt-1">
                            {programs.map((program) => {
                              const Icon = program.icon;
                              return (
                                <Button
                                  key={program.id}
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start gap-3 text-sm hover:bg-primary hover:text-primary-foreground"
                                  onClick={() => {
                                    onOpenApp(program.id, program.label);
                                    onClose();
                                  }}
                                >
                                  <Icon size={28} />
                                  {program.label}
                                </Button>
                              );
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave MapleOS?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleLogout}>
              Logout
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};